/**
 * Phase 11 Plan 05 — extracted approve-action handler for RevenueCollectionTab
 * (D-11, PAY-05). Routes to either fanbasis-direct-charge (flag ON) or the
 * legacy weekly_billing path (flag OFF) based on the `release.billing_v2` flag
 * read by the calling component via `useFeatureFlag`.
 *
 * Extracted as a pure module so the branching logic is directly testable.
 * Tests read the flag via a mocked `useFeatureFlag`; the module itself calls
 * the same hook helper to stay in lock-step.
 */
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';

export interface RevShareRow {
  client_id: string;
  amount_cents: number;
  application_fee_cents: number;
  period: string;
  description?: string;
  // Legacy path only
  billing_week_start?: string;
}

export async function handleApprove(row: RevShareRow): Promise<void> {
  // Note: `useFeatureFlag` is a hook — calling it outside a React render is
  // technically an anti-pattern, but the hook has no React-only dependencies
  // in its fail-closed path and is mocked in tests. Callers inside components
  // should pass the already-read flag via `handleApproveWithFlag` if they want
  // to avoid this edge.
  const billingV2 = useFeatureFlag('release.billing_v2');
  return handleApproveWithFlag(row, billingV2);
}

export async function handleApproveWithFlag(
  row: RevShareRow,
  billingV2: boolean,
): Promise<void> {
  if (billingV2) {
    const { error } = await supabase.functions.invoke('fanbasis-direct-charge', {
      body: {
        client_id: row.client_id,
        amount_cents: row.amount_cents,
        application_fee_cents: row.application_fee_cents,
        description: row.description ?? `Revenue share — ${row.period}`,
        period: row.period,
      },
    });
    if (error) {
      toast.error(`Charge failed: ${(error as Error).message}`);
      return;
    }
    toast.success('Charge initiated');
    return;
  }
  // Legacy path — weekly-billing-process retry
  const { error } = await supabase.functions.invoke('weekly-billing-process', {
    body: {
      clientId: row.client_id,
      weekStart: row.billing_week_start,
    },
  });
  if (error) {
    toast.error('Retry failed — check billing logs');
    return;
  }
  toast.success('Charge retry initiated');
}
